import React, { useState, useRef } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

import Select from '../../../components/ui/Select';
import { storageService } from '../../../services/storageService';
import { generateVideoThumbnailFile } from '../../../utils/videoThumbnail';

// Maximum file size: 500MB (limite do servidor, pode aumentar conforme necessário)
const MAX_FILE_SIZE = 500 * 1024 * 1024; // 500MB in bytes

const UploadModal = ({ isOpen, onClose, onUpload }) => {
  const [uploadFiles, setUploadFiles] = useState([]);
  const [uploadProgress, setUploadProgress] = useState({});
  const [uploadErrors, setUploadErrors] = useState({});
  const [formData, setFormData] = useState({
    category: '',
    price: 0,
    watermarkEnabled: true,
    format: 'auto' // auto, story, post, landscape, portrait
  });
  const fileInputRef = useRef(null);

  const categoryOptions = [
    { value: 'movies-series', label: 'Artes de Filmes e Séries' },
    { value: 'bbb', label: 'Artes do Big Brother Brasil' },
    { value: 'brasileirao', label: 'Artes do Brasileirão' },
    { value: 'app-partnerships', label: 'Artes de Parcerias de Aplicativos' }
  ];

  const getFormatFromDimensions = (width, height) => {
    const ratio = width / height;
    
    // Story format (9:16 ratio, vertical)
    if (ratio >= 0.5 && ratio <= 0.6) {
      return 'story';
    }
    // Post format (1:1 ratio, square)
    else if (ratio >= 0.95 && ratio <= 1.05) {
      return 'post';
    }
    // Landscape (16:9 or wider)
    else if (ratio > 1.5) {
      return 'landscape';
    }
    // Portrait (taller than square but not story)
    else if (ratio < 0.95) {
      return 'portrait';
    }
    // Default
    return 'custom';
  };

  const analyzeMediaDimensions = (file) => {
    return new Promise((resolve) => {
      const isVideo = file?.type?.startsWith('video/');
      
      if (isVideo) {
        const video = document.createElement('video');
        video.preload = 'metadata';
        video.onloadedmetadata = () => {
          URL.revokeObjectURL(video.src);
          const format = getFormatFromDimensions(video.videoWidth, video.videoHeight);
          resolve({ width: video.videoWidth, height: video.videoHeight, format });
        };
        video.onerror = () => resolve({ width: 0, height: 0, format: 'custom' });
        video.src = URL.createObjectURL(file);
      } else {
        const img = new Image();
        img.onload = () => {
          URL.revokeObjectURL(img.src);
          const format = getFormatFromDimensions(img.width, img.height);
          resolve({ width: img.width, height: img.height, format });
        };
        img.onerror = () => resolve({ width: 0, height: 0, format: 'custom' });
        img.src = URL.createObjectURL(file);
      }
    });
  };

  const handleFileSelect = async (e) => {
    const files = Array.from(e?.target?.files || []);
    
    // Analyze dimensions for each file
    const filesWithMetadata = await Promise.all(
      files?.map(async (file) => {
        const dimensions = await analyzeMediaDimensions(file);
        const isTooBig = file?.size > MAX_FILE_SIZE;
        return {
          file,
          id: Math.random()?.toString(36)?.substr(2, 9),
          name: file?.name,
          size: file?.size,
          type: file?.type?.startsWith('video/') ? 'video' : 'image',
          preview: URL.createObjectURL(file),
          dimensions,
          error: isTooBig ? `Arquivo excede o limite de ${formatFileSize(MAX_FILE_SIZE)}` : null
        };
      })
    );
    
    setUploadFiles(prev => [...prev, ...filesWithMetadata]);
  };

  const handleDrop = async (e) => {
    e?.preventDefault();
    const files = Array.from(e?.dataTransfer?.files);
    
    // Analyze dimensions for each file
    const filesWithMetadata = await Promise.all(
      files?.map(async (file) => {
        const dimensions = await analyzeMediaDimensions(file);
        const isTooBig = file?.size > MAX_FILE_SIZE;
        return {
          file,
          id: Math.random()?.toString(36)?.substr(2, 9),
          name: file?.name,
          size: file?.size,
          type: file?.type?.startsWith('video/') ? 'video' : 'image',
          preview: URL.createObjectURL(file),
          dimensions,
          error: isTooBig ? `Arquivo excede o limite de ${formatFileSize(MAX_FILE_SIZE)}` : null
        };
      })
    );
    
    setUploadFiles(prev => [...prev, ...filesWithMetadata]);
  };

  const handleDragOver = (e) => {
    e?.preventDefault();
  };

  const removeFile = (id) => {
    setUploadFiles(prev => prev?.filter(f => f?.id !== id));
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    let i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes?.[i];
  };

  const handleUpload = async () => {
    if (uploadFiles?.length === 0 || !formData?.category) return;

    // Check for files with errors
    const filesWithErrors = uploadFiles?.filter(f => f?.error);
    if (filesWithErrors?.length > 0) {
      alert('Por favor, remova os arquivos com erro antes de fazer upload.');
      return;
    }

    try {
      // Upload files to Supabase Storage
      const uploadResults = [];
      
      for (let i = 0; i < uploadFiles?.length; i++) {
        const fileData = uploadFiles?.[i];
        
        // Skip files with errors
        if (fileData?.error) continue;
        
        // Update progress to show uploading
        setUploadProgress(prev => ({ ...prev, [fileData?.id]: 10 }));
        
        try {
          // Determine folder based on file type
          const folder = fileData?.type === 'video' ? 'videos' : 'images';
          
          // Upload main file to Supabase Storage
          const { url, path } = await storageService?.uploadFile(fileData?.file, folder);
          
          // For videos, also upload thumbnail if it's an image (first frame extraction would require backend)
          let thumbnailUrl = null;
          if (fileData?.type === 'video') {
            // Use the main thumbnail as thumbnailUrl for now
            // In production, you could extract first frame or allow separate thumbnail upload
            thumbnailUrl = url;
          }
          
          // Update progress
          setUploadProgress(prev => ({ ...prev, [fileData?.id]: 100 }));
          
          // Prepare data for database
          uploadResults?.push({
            ...fileData,
            url, // This is the actual storage URL
            thumbnailUrl, // Custom thumbnail URL for videos
            storagePath: path
          });
        } catch (uploadError) {
          console.error(`Error uploading file ${fileData?.name}:`, uploadError);
          setUploadProgress(prev => ({ ...prev, [fileData?.id]: -1 }));
          setUploadErrors(prev => ({ ...prev, [fileData?.id]: uploadError?.message || 'Erro ao fazer upload' }));
          throw uploadError;
        }
      }
      
      // Auto-generate thumbnails for videos that don't have custom thumbnails
      for (let i = 0; i < uploadResults?.length; i++) {
        const result = uploadResults?.[i];
        
        // Only generate thumbnail for videos without custom thumbnailUrl
        if (result?.type === 'video' && !result?.thumbnailUrl) {
          try {
            setUploadProgress(prev => ({ ...prev, [result?.id]: 50 }));
            
            // Generate thumbnail from video (first frame at 1 second)
            const thumbnailFile = await generateVideoThumbnailFile(
              result?.file,
              `${result?.name?.split('.')?.[0]}_thumb.jpg`,
              1 // Capture at 1 second
            );
            
            // Upload generated thumbnail to storage
            const { url: thumbUrl } = await storageService?.uploadFile(thumbnailFile, 'thumbnails');
            
            // Update result with generated thumbnail
            uploadResults[i].thumbnailUrl = thumbUrl;
            
            setUploadProgress(prev => ({ ...prev, [result?.id]: 100 }));
          } catch (thumbError) {
            console.error(`Error generating thumbnail for ${result?.name}:`, thumbError);
            // Don't fail the entire upload, just log the error
            // Video will use fallback thumbnail in UI
          }
        }
      }
      
      // Call parent handler with uploaded files
      await onUpload({
        files: uploadResults,
        category: formData?.category,
        price: formData?.price,
        watermarkEnabled: formData?.watermarkEnabled
      });
      
      handleClose();
    } catch (error) {
      console.error('Upload error:', error);
      const errorMessage = error?.message?.includes('exceeded') 
        ? `Arquivo muito grande. O limite é ${formatFileSize(MAX_FILE_SIZE)}.`
        : `Erro ao fazer upload: ${error?.message}`;
      alert(errorMessage);
    }
  };

  const handleClose = () => {
    setUploadFiles([]);
    setUploadProgress({});
    setUploadErrors({});
    setFormData({ category: '', price: 0, watermarkEnabled: true });
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[300] flex items-center justify-center p-4 bg-background/80 backdrop-blur-sm">
      <div className="w-full max-w-4xl bg-card rounded-lg glow-primary max-h-[90vh] overflow-hidden flex flex-col">
        <div className="p-4 md:p-6 border-b border-border">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-md bg-accent/10 flex items-center justify-center glow-accent">
                <Icon name="Upload" size={20} color="var(--color-accent)" />
              </div>
              <div>
                <h2 className="text-lg md:text-xl font-heading font-bold text-accent">
                  Upload de Conteúdo
                </h2>
                <p className="text-xs md:text-sm text-muted-foreground">
                  Adicione novas artes e vídeos à biblioteca
                </p>
              </div>
            </div>
            <Button
              variant="ghost"
              size="sm"
              iconName="X"
              onClick={handleClose}
            >
              Fechar
            </Button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto custom-scrollbar p-4 md:p-6">
          <div className="space-y-6">
            <div
              onDrop={handleDrop}
              onDragOver={handleDragOver}
              className="border-2 border-dashed border-border rounded-lg p-8 text-center hover:border-primary transition-smooth cursor-pointer"
              onClick={() => fileInputRef?.current?.click()}
            >
              <div className="flex flex-col items-center gap-4">
                <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center glow-primary">
                  <Icon name="Upload" size={32} color="var(--color-primary)" />
                </div>
                <div>
                  <p className="text-sm font-medium text-foreground mb-1">
                    Clique para selecionar ou arraste arquivos aqui
                  </p>
                  <p className="text-xs text-muted-foreground">
                    Suporta imagens (JPG, PNG, GIF) e vídeos (MP4, MOV, AVI)
                  </p>
                  <p className="text-xs text-red-500 font-medium mt-1">
                    Limite máximo: {formatFileSize(MAX_FILE_SIZE)} por arquivo
                  </p>
                </div>
                <Button
                  type="button"
                  variant="outline"
                  iconName="FolderOpen"
                  size="sm"
                >
                  Selecionar Arquivos
                </Button>
              </div>
              <input
                ref={fileInputRef}
                type="file"
                multiple
                accept="image/*,video/*"
                onChange={handleFileSelect}
                className="hidden"
              />
            </div>

            {uploadFiles?.length > 0 && (
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <h3 className="text-sm font-heading font-bold text-foreground">
                    Arquivos Selecionados ({uploadFiles?.length})
                  </h3>
                  <Button
                    variant="ghost"
                    size="sm"
                    iconName="Trash2"
                    onClick={() => setUploadFiles([])}
                  >
                    Limpar Todos
                  </Button>
                </div>

                <div className="space-y-2">
                  {uploadFiles?.map((file) => (
                    <div
                      key={file?.id}
                      className={`flex items-center gap-4 p-3 bg-background rounded-lg border ${
                        file?.error ? 'border-red-500' : 'border-border'
                      }`}
                    >
                      <div className="w-16 h-16 rounded-md overflow-hidden flex-shrink-0 bg-muted">
                        {file?.type === 'video' ? (
                          <div className="w-full h-full flex items-center justify-center">
                            <Icon name="Play" size={24} color="var(--color-primary)" />
                          </div>
                        ) : (
                          <img
                            src={file?.preview}
                            alt={`Preview of ${file?.name}`}
                            className="w-full h-full object-cover"
                          />
                        )}
                      </div>

                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-foreground line-clamp-1">
                          {file?.name}
                        </p>
                        <div className="flex items-center gap-2 mt-1">
                          <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                            file?.type === 'video' ?'bg-secondary/20 text-secondary' :'bg-primary/20 text-primary'
                          }`}>
                            {file?.type === 'video' ? 'Vídeo' : 'Imagem'}
                          </span>
                          <span className={`text-xs ${
                            file?.error ? 'text-red-500 font-medium' : 'text-muted-foreground'
                          }`}>
                            {formatFileSize(file?.size)}
                          </span>
                        </div>

                        {file?.error && (
                          <div className="mt-2 flex items-center gap-1 text-red-500">
                            <Icon name="AlertCircle" size={14} />
                            <span className="text-xs font-medium">{file?.error}</span>
                          </div>
                        )}

                        {uploadProgress?.[file?.id] !== undefined && !file?.error && (
                          <div className="mt-2">
                            <div className="w-full h-1.5 bg-muted rounded-full overflow-hidden">
                              <div
                                className="h-full bg-primary transition-all duration-300"
                                style={{ width: `${uploadProgress?.[file?.id]}%` }}
                              />
                            </div>
                            <p className="text-xs text-muted-foreground mt-1">
                              {uploadProgress?.[file?.id]}% concluído
                            </p>
                          </div>
                        )}
                      </div>

                      <Button
                        variant="ghost"
                        size="sm"
                        iconName="X"
                        onClick={() => removeFile(file?.id)}
                      />
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="space-y-4 pt-4 border-t border-border">
              <Select
                label="Categoria"
                options={categoryOptions}
                value={formData?.category}
                onChange={(value) => setFormData(prev => ({ ...prev, category: value }))}
                placeholder="Selecione a categoria"
                required
              />

              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Preço (R$)
                </label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={formData?.price}
                  onChange={(e) => setFormData(prev => ({ ...prev, price: parseFloat(e?.target?.value) || 0 }))}
                  className="w-full px-4 py-2 bg-background border border-border rounded-md text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary transition-smooth"
                  placeholder="0.00"
                />
                <p className="text-xs text-muted-foreground mt-1">
                  Defina o preço do conteúdo em reais
                </p>
              </div>

              <div className="flex items-center justify-between p-4 bg-background rounded-lg border border-border">
                <div>
                  <p className="text-sm font-medium text-foreground">
                    Aplicar marca d'água automaticamente
                  </p>
                  <p className="text-xs text-muted-foreground mt-1">
                    Proteja seu conteúdo com o logo UP MIDIA
                  </p>
                </div>
                <div className="flex items-center gap-3">
                  {formData?.watermarkEnabled && (
                    <img 
                      src="/assets/images/up_menor-1769135382338.png" 
                      alt="UP MIDIA Logo" 
                      className="h-8 w-auto object-contain"
                    />
                  )}
                  <input
                    type="checkbox"
                    checked={formData?.watermarkEnabled}
                    onChange={(e) => setFormData(prev => ({ ...prev, watermarkEnabled: e?.target?.checked }))}
                    className="w-5 h-5 rounded border-2 border-primary bg-background checked:bg-primary cursor-pointer"
                  />
                </div>
              </div>

              {/* Format Classification */}
              <div className="space-y-3">
                <label className="block text-sm font-medium text-foreground">
                  Formato do Conteúdo
                </label>
                <select
                  value={formData?.format}
                  onChange={(e) => setFormData(prev => ({ ...prev, format: e?.target?.value }))}
                  className="w-full px-4 py-2 bg-background border border-border rounded-md text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary transition-smooth"
                >
                  <option value="auto">Automático (detectar por dimensões)</option>
                  <option value="story">Story (9:16 - Vertical)</option>
                  <option value="post">Post (1:1 - Quadrado)</option>
                  <option value="landscape">Landscape (16:9 - Horizontal)</option>
                  <option value="portrait">Portrait (Vertical)</option>
                  <option value="custom">Personalizado</option>
                </select>
                <p className="text-xs text-muted-foreground">
                  {formData?.format === 'auto' ?'O formato será detectado automaticamente baseado nas dimensões' :'Formato manual selecionado para todos os arquivos'}
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="p-4 md:p-6 border-t border-border">
          <div className="flex flex-col sm:flex-row gap-3">
            <Button
              variant="default"
              iconName="Upload"
              onClick={handleUpload}
              disabled={uploadFiles?.length === 0 || !formData?.category}
              fullWidth
            >
              Fazer Upload ({uploadFiles?.length})
            </Button>
            <Button
              variant="outline"
              iconName="X"
              onClick={handleClose}
              fullWidth
            >
              Cancelar
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UploadModal;